import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
interface PlayerSummaryCardProps {
  playerNumber: number;
}
export function PlayerSummaryCard({
  playerNumber
}: PlayerSummaryCardProps) {
  const [playerName, setPlayerName] = useState(`Player ${playerNumber}`);
  const [netWorth, setNetWorth] = useState(0);
  const [cashFlow, setCashFlow] = useState(0);
  const [monthlyIncome, setMonthlyIncome] = useState(0);
  const [monthlyExpenses, setMonthlyExpenses] = useState(0);
  const [lastUpdated, setLastUpdated] = useState(Date.now());
  // Load data from localStorage
  const loadPlayerData = () => {
    const savedData = localStorage.getItem(`player${playerNumber}Data`);
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);
        setPlayerName(parsedData.playerName || `Player ${playerNumber}`);
        setMonthlyIncome(parsedData.totalIncome || 0);
        setMonthlyExpenses(parsedData.totalExpenses || 0);
        // Calculate derived values
        const totalAssets = parsedData.totalAssets || 0;
        const totalLiabilities = parsedData.totalLiabilities || 0;
        setNetWorth(totalAssets - totalLiabilities);
        setCashFlow((parsedData.totalIncome || 0) - (parsedData.totalExpenses || 0));
        setLastUpdated(Date.now());
      } catch (error) {
        console.error(`Error loading data for Player ${playerNumber}:`, error);
      }
    }
  };
  // Initial load and set up event listeners
  useEffect(() => {
    // Initial load
    loadPlayerData();
    // Set up event listener for storage changes (for cross-tab updates)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === `player${playerNumber}Data`) {
        loadPlayerData();
      }
    };
    window.addEventListener('storage', handleStorageChange);
    // Custom event for same-tab updates
    const handleCustomEvent = () => {
      loadPlayerData();
    };
    // Listen for the custom event
    window.addEventListener(`player${playerNumber}Updated`, handleCustomEvent);
    // Set up an interval to periodically check for updates
    const intervalId = setInterval(loadPlayerData, 2000);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener(`player${playerNumber}Updated`, handleCustomEvent);
      clearInterval(intervalId);
    };
  }, [playerNumber]);
  return <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-100" data-id="element-187">
      <div className="flex justify-between items-center mb-3" data-id="element-188">
        <h2 className="text-lg font-semibold text-gray-800" data-id="element-189">
          {playerName}'s Financial Summary
        </h2>
        <Link to={`/player${playerNumber}`} className="text-green-600 hover:text-green-700 flex items-center text-sm font-medium" data-id="element-190">
          View Details
          <ArrowRight size={16} className="ml-1" data-id="element-191" />
        </Link>
      </div>
      <div className="grid grid-cols-2 gap-3" data-id="element-192">
        <div className="bg-gray-50 p-3 rounded-md" data-id="element-193">
          <p className="text-xs text-gray-500" data-id="element-194">Net Worth</p>
          <p className="text-lg font-bold text-green-600" data-id="element-195">
            ${netWorth.toLocaleString()}
          </p>
        </div>
        <div className="bg-gray-50 p-3 rounded-md" data-id="element-196">
          <p className="text-xs text-gray-500" data-id="element-197">Cash Flow</p>
          <p className="text-lg font-bold text-green-600" data-id="element-198">
            ${cashFlow.toLocaleString()}
          </p>
        </div>
        <div className="bg-gray-50 p-3 rounded-md" data-id="element-199">
          <p className="text-xs text-gray-500" data-id="element-200">Monthly Income</p>
          <p className="text-lg font-bold text-gray-800" data-id="element-201">
            ${monthlyIncome.toLocaleString()}
          </p>
        </div>
        <div className="bg-gray-50 p-3 rounded-md" data-id="element-202">
          <p className="text-xs text-gray-500" data-id="element-203">Monthly Expenses</p>
          <p className="text-lg font-bold text-gray-800" data-id="element-204">
            ${monthlyExpenses.toLocaleString()}
          </p>
        </div>
      </div>
    </div>;
}